import { ReactNode, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toaster } from 'baseui/toast';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { AccessDenied } from './AccessDenied';
import { matrixRoleOf, roleCan, type FdAction, type FdSection } from '../utils/frontDeskMatrix';

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requirePropertyManager?: boolean;
  requireInvitationManager?: boolean;
  requireTenantSettings?: boolean;
  requirePaymentsManager?: boolean;
  requireUsersManager?: boolean;
  requireEstatesManager?: boolean;
  requireEventsManager?: boolean;
  // Looser gate for the read-only event dashboard: events managers OR the
  // dedicated event-dashboard-only role (business_event_dashboard).
  requireEventDashboardViewer?: boolean;
  requireInsightsManager?: boolean;
  requireCMSManager?: boolean;
  requireRolesManager?: boolean;
  /** SAM-535 — holds the backend's `xhinola_admin` group. */
  requireXhinolaAdmin?: boolean;
  requireServicesManager?: boolean;
  requireBookingsManager?: boolean;
  requireBusinessAccess?: boolean;
  /**
   * SAM-553 — Arrivals is a read-only merge of Bookings + Invitations, so it
   * opens to anyone who can see either source: bookings managers (incl. the
   * front-desk groups isBookingsManager already folds in) or any invitation
   * manager flavor / property manager.
   */
  requireArrivalsViewer?: boolean;
  /**
   * The Front Desk matrix section this route belongs to (specs/front-desk-tenant-permisos.md). For the tenant
   * Front Desk and the estate group roles (SAM-1108) their matrix decides (`fdAction`, default view) and the
   * other checks are skipped; everyone else is unaffected. A route without it keeps the old rule, which refuses
   * them unless one of their groups grants it.
   */
  fdSection?: FdSection;
  fdAction?: FdAction;
}

export const ProtectedRoute = ({
  children,
  requireAdmin = false,
  requirePropertyManager = false,
  requireInvitationManager = false,
  requireTenantSettings = false,
  requireXhinolaAdmin = false,
  requirePaymentsManager = false,
  requireUsersManager = false,
  requireEstatesManager = false,
  requireEventsManager = false,
  requireEventDashboardViewer = false,
  requireInsightsManager = false,
  requireCMSManager = false,
  requireRolesManager = false,
  requireServicesManager = false,
  requireBookingsManager = false,
  requireBusinessAccess = false,
  requireArrivalsViewer = false,
  fdSection,
  fdAction = 'view',
}: ProtectedRouteProps) => {
  const navigate = useNavigate();
  const permissions = useUserPermissions();
  // undefined: not a matrix role (or a route outside the matrix), the checks below decide.
  const matrixRole = matrixRoleOf(permissions);
  const fdDecision: boolean | undefined = matrixRole && fdSection ? roleCan(matrixRole, fdSection, fdAction) : undefined;
  // The Property Manager gets the bookings grant as front desk, but has no Business (Rodrigo, 29 Oct): /business/*
  // and Activity are closed to it unless it also holds a business group of its own.
  const isPropertyManagerOnly = permissions.isPropertyManager && !permissions.isAdmin
    && !permissions.isPaymentsManager && !permissions.isEventsManager && !permissions.isServicesManager;
  const mayOpenBusiness = permissions.isAdmin || permissions.isPaymentsManager || permissions.isEventsManager
    || permissions.isServicesManager || (permissions.isBookingsManager && !isPropertyManagerOnly);

  useEffect(() => {
    if (permissions.isLoading) {
      return;
    }

    if (fdDecision === true) return;
    if (fdDecision === false) {
      toaster.negative('You do not have permission to access this page', {
        autoHideDuration: 3000,
      });
      navigate('/invitations');
      return;
    }

    // Helper function to check permission
    const hasPermission = (
      requirePermission: boolean,
      userHasPermission: boolean
    ): boolean => {
      if (!requirePermission) return true;
      return permissions.isAdmin || userHasPermission;
    };

    // Check all permissions
    const checks = [
      { require: requireAdmin, has: permissions.isAdmin, name: 'admin' },
      { require: requireInvitationManager, has: permissions.isInvitationManager || permissions.isResidentialInvitationManager || permissions.isAllInvitationManager || permissions.isPropertyManager || permissions.isBusinessAdmin, name: 'invitation manager' },
      { require: requireTenantSettings, has: permissions.isTenantSettings, name: 'tenant settings' },
      { require: requireXhinolaAdmin, has: permissions.isXhinolaAdmin, name: 'xhinola admin' },
      { require: requirePaymentsManager, has: permissions.isPaymentsManager, name: 'payments manager' },
      { require: requireUsersManager, has: permissions.isUsersManager, name: 'users manager' },
      { require: requireEstatesManager, has: permissions.isEstatesManager, name: 'estates manager' },
      { require: requireEventsManager, has: permissions.isEventsManager, name: 'events manager' },
      { require: requireEventDashboardViewer, has: permissions.isEventsManager || permissions.isEventDashboardOnly, name: 'event dashboard viewer' },
      { require: requireInsightsManager, has: permissions.isInsightsManager, name: 'insights manager' },
      { require: requireCMSManager, has: permissions.isCMSManager, name: 'CMS manager' },
      { require: requireRolesManager, has: permissions.isRolesManager, name: 'roles manager' },
      { require: requireServicesManager, has: permissions.isServicesManager, name: 'services manager' },
    ];

    // Special case: businessAccess allows any of several permissions
    // (kept in sync with the render-time check below, which also allows isBookingsManager —
    // otherwise a bookings-only user bounces straight back out with an error toast)
    if (requireBusinessAccess && !mayOpenBusiness) {
      toaster.negative('You do not have permission to access this page', {
        autoHideDuration: 3000,
      });
      navigate(permissions.isInvitationOnly ? '/invitations' : '/dashboard');
      return;
    }

    // Special case: arrivalsViewer (SAM-553) — anyone who can see Bookings OR
    // Invitations gets Arrivals too, since it only reads and links to those pages.
    if (
      requireArrivalsViewer &&
      !permissions.isAdmin &&
      !permissions.isBookingsManager &&
      !permissions.isInvitationManager &&
      !permissions.isResidentialInvitationManager &&
      !permissions.isAllInvitationManager &&
      !permissions.isPropertyManager
    ) {
      toaster.negative('You do not have permission to access this page', {
        autoHideDuration: 3000,
      });
      navigate(permissions.isInvitationOnly ? '/invitations' : '/dashboard');
      return;
    }

    // Special case: if requireAdmin is true, only admin can access
    if (requireAdmin && !permissions.isAdmin) {
      toaster.negative('You do not have permission to access this page', {
        autoHideDuration: 3000,
      });
      navigate(permissions.isInvitationOnly ? '/invitations' : '/dashboard');
      return;
    }

    // Special case: property manager access
    if (requirePropertyManager && !permissions.isAdmin && !permissions.isPropertyManager) {
      toaster.negative('You do not have permission to access this page', {
        autoHideDuration: 3000,
      });
      navigate(permissions.isInvitationOnly ? '/invitations' : '/dashboard');
      return;
    }

    // For other permissions, admin OR specific permission is allowed
    for (const check of checks) {
      if (check.require && check.name !== 'admin' && !hasPermission(check.require, check.has)) {
        toaster.negative('You do not have permission to access this page', {
          autoHideDuration: 3000,
        });
        navigate(permissions.isInvitationOnly ? '/invitations' : '/dashboard');
        return;
      }
    }
  }, [
    permissions,
    fdDecision,
    requireAdmin,
    requirePropertyManager,
    requireInvitationManager,
    requireTenantSettings,
    requirePaymentsManager,
    requireUsersManager,
    requireEstatesManager,
    requireEventsManager,
    requireEventDashboardViewer,
    requireInsightsManager,
    requireCMSManager,
    requireRolesManager,
    requireServicesManager,
    requireBusinessAccess,
    requireArrivalsViewer,
    mayOpenBusiness,
    navigate,
  ]);

  // Mientras carga, no mostrar nada
  if (permissions.isLoading) {
    return null;
  }

  if (fdDecision === true) return <>{children}</>;
  if (fdDecision === false) return <AccessDenied />;

  // Helper function to check permission
  const hasPermission = (
    requirePermission: boolean,
    userHasPermission: boolean
  ): boolean => {
    if (!requirePermission) return true;
    return permissions.isAdmin || userHasPermission;
  };

  // SAM-544 (N03): every denial below renders a coherent AccessDenied view
  // instead of `null` — a direct hit on a prohibited URL must never show a
  // blank screen while (or instead of) the redirect above runs.
  // Check all permissions before rendering
  if (requireAdmin && !permissions.isAdmin) {
    return <AccessDenied />;
  }

  if (requirePropertyManager && !permissions.isAdmin && !permissions.isPropertyManager) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireInvitationManager, permissions.isInvitationManager || permissions.isResidentialInvitationManager || permissions.isAllInvitationManager || permissions.isPropertyManager || permissions.isBusinessAdmin)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireTenantSettings, permissions.isTenantSettings)) {
    return <AccessDenied />;
  }

  // Courtesy only: the backend also requires an Xhinola operator pool, which the browser cannot
  // see, so the merchant endpoint denies regardless of what this decides.
  if (!hasPermission(requireXhinolaAdmin, permissions.isXhinolaAdmin)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requirePaymentsManager, permissions.isPaymentsManager)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireUsersManager, permissions.isUsersManager)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireEstatesManager, permissions.isEstatesManager)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireEventsManager, permissions.isEventsManager)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireEventDashboardViewer, permissions.isEventsManager || permissions.isEventDashboardOnly)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireInsightsManager, permissions.isInsightsManager)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireCMSManager, permissions.isCMSManager)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireRolesManager, permissions.isRolesManager)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireServicesManager, permissions.isServicesManager)) {
    return <AccessDenied />;
  }

  if (!hasPermission(requireBookingsManager, permissions.isBookingsManager && !isPropertyManagerOnly)) {
    return <AccessDenied />;
  }

  if (requireBusinessAccess && !mayOpenBusiness) {
    return <AccessDenied />;
  }

  if (
    requireArrivalsViewer &&
    !permissions.isAdmin &&
    !permissions.isBookingsManager &&
    !permissions.isInvitationManager &&
    !permissions.isResidentialInvitationManager &&
    !permissions.isAllInvitationManager &&
    !permissions.isPropertyManager
  ) {
    return <AccessDenied />;
  }

  return <>{children}</>;
};

